import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Bug, X, Send, GripVertical } from 'lucide-react';

const BugReportFloatingButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bugDescription, setBugDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 45, y: window.innerHeight - 45 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const { toast } = useToast();

  const handleMouseDown = (e) => {
    if (e.target.closest('.bug-icon-only')) {
      setIsDragging(true);
      const startX = e.clientX - position.x;
      const startY = e.clientY - position.y;

      const handleMouseMove = (e) => {
        const newX = Math.max(0, Math.min(window.innerWidth - 35, e.clientX - startX));
        const newY = Math.max(0, Math.min(window.innerHeight - 35, e.clientY - startY));
        setPosition({ x: newX, y: newY });
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleClick = (e) => {
    if (!isDragging && e.target.closest('.bug-icon-only')) {
      setIsModalOpen(true);
    }
  };

  const gatherUserContext = () => {
    return {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      localStorage: {
        // Only include non-sensitive data
        theme: localStorage.getItem('theme'),
        lastLocation: localStorage.getItem('lastLocation')
      },
      sessionData: {
        sessionId: sessionStorage.getItem('sessionId') || 'anonymous',
        referrer: document.referrer
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bugDescription.trim()) {
      toast({
        title: "Please describe the bug",
        description: "We need some details to help you better.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const userContext = gatherUserContext();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: bugDescription,
          userContext: userContext
        })
      });

      if (response.ok) {
        toast({
          title: "Bug report submitted!",
          description: "Thank you for helping us improve the app. We'll look into this right away.",
          variant: "default"
        });
        setBugDescription('');
        setIsModalOpen(false);
      } else {
        throw new Error('Failed to submit bug report');
      }
    } catch (error) {
      console.error('Error submitting bug report:', error);
      toast({
        title: "Failed to submit report",
        description: "There was an error submitting your bug report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Bug Report Button */}
      <div
        ref={dragRef}
        className="fixed z-50 cursor-pointer select-none"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transition: isDragging ? 'none' : 'all 0.2s ease-out'
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <div className="bug-icon-only group relative">
          {/* Drag Handle */}
          <div className="absolute -top-2 -left-2 opacity-0 group-hover:opacity-50 transition-opacity duration-200">
            <GripVertical className="h-3 w-3 text-gray-400" />
          </div>
          
          {/* Bug Icon in Chat Bubble */}
          <div className="bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 w-[35px] h-[35px] flex items-center justify-center">
            <Bug className="h-4 w-4" />
          </div>
          
          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            Report a bug
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
          </div>
        </div>
      </div>

      {/* Bug Report Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" />
              Report a Bug
            </DialogTitle>
            <DialogDescription>
              Help us improve Studio Manager V5 by reporting any issues you encounter.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bug-description" className="text-sm font-medium">
                What happened? *
              </Label>
              <Textarea
                id="bug-description"
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value)}
                placeholder="Please describe what you were doing when the bug occurred, what you expected to happen, and what actually happened..."
                className="min-h-[120px] resize-none"
                disabled={isSubmitting}
                required
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>📍 Context:</strong> We'll automatically include your current page, browser info, and recent actions to help us debug faster.
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !bugDescription.trim()}
                className="bg-red-500 hover:bg-red-600"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Report
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReportFloatingButton;